// eslint-disable-next-line no-restricted-imports
import {jsx} from 'panel';
import produce from 'immer';

// @ts-strict-ignore
import {MetricsResource} from 'common/report/model-resources/metrics';
import {ActionType, ContextPanelData, DetailType} from 'common/widgets/context-panel/types';
import {ModelStatus} from 'common/util/model-resource/model-resource';
import {getEditCapability, getShareCapability} from 'common/report/util/saved-entities';
import {EntityType} from 'common/report/util/saved-entities/types';
import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {
  getBaseColorFromMetricType,
  getIconForSavedMetric,
  getMetricBehaviorType,
  MetricTypeIcons,
} from 'common/report/insights/util/metrics';
import {getCountByForMetric, getSelectedCountByLabel} from 'common/query-builder/query-block/count-menu/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {LocalizationStore} from 'common/i18n/store';
import {
  BehaviorDefinitionPanelEl,
  Props as BehaviorDefinitionPanelProps,
} from 'iron/common/widgets/context-panel/behavior-definition-panel';
import {rgbaStringFromHexString} from 'common/util/colors';
import {isSaveableBehavior} from 'common/report/insights/contexts/utils/behaviors';
import {BehaviorMetric} from 'app_api/projects/metrics/types';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {getSelectedGroupLabel} from 'common/query-builder/query-entry-section/util';
import type {ProjectUsersResource} from 'common/report/model-resources/project-users';
import {isCanvasEnabled} from 'common/report/util/feature-sampling';
import {CurrentProject} from 'common/report/model-resources/projects';
import {SavedMetricMenuItemValue} from 'common/query-builder/query-block/multi-metrics-menu/types';
import {MetricType} from 'common/types/reports/bookmark';

function getBackgroundColorForMetric(metricType: MetricType) {
  return rgbaStringFromHexString(getBaseColorFromMetricType(metricType), 0.04);
}

/**
 * Generates the context panel data for Metrics to allow for sharing and description editing
 */
export function generateSavedMetricContextPanelData({
  item,
  metricsContext,
  behaviorsContext,
  dataGroups,
  i18n,
  projectUsers,
  currentProject,
  canWriteMetrics = false,
  canShare = false,
}: {
  item: SavedMetricMenuItemValue;
  metricsContext: MetricsResource;
  behaviorsContext: BehaviorsResource;
  dataGroups: DataGroup[];
  i18n: LocalizationStore;
  projectUsers: ProjectUsersResource;
  currentProject: CurrentProject;
  canWriteMetrics?: boolean;
  canShare?: boolean;
}): ContextPanelData {
  if (!item.id) {
    const metricType = item.definition.behavior.type;
    return {
      header: item.name,
      actions: [],
      links: [],
      details: [],
      subHeader: ({t}) => t(`query-builder.metrics.unsaved-metric`),
      entityIcon: MetricTypeIcons[metricType],
      backgroundColor: getBackgroundColorForMetric(metricType),
      description: item.description,
      entityType: EntityType.Metric,
      excludedMetricIcons: [`pie`, `pulse`],
    };
  }
  const canvasEnabled = isCanvasEnabled(currentProject);

  const model = metricsContext.getOne(item.id);
  let metric = model.value as BehaviorMetric | undefined;

  // metric.definition can be undefined if it's a cached metric that's been deleted.
  // see https://mixpanel.atlassian.net/browse/AE-1494
  if (model.status === ModelStatus.Loading || !metric?.definition) {
    return null;
  }

  const permissions = getEditCapability(metric);
  const actions: ContextPanelData[`actions`] = [];
  const links: ContextPanelData[`links`] = [];
  const details: ContextPanelData[`details`] = [];

  if (metric.definition.behavior.id && !metric.definition.behavior.hasUnsavedChanges) {
    const behaviorModel = behaviorsContext.getOne(metric.definition.behavior.id);
    const behavior = behaviorModel.value;
    if (behaviorModel.status !== ModelStatus.Loading && behavior) {
      metric = produce(metric, (draft) => {
        draft.definition.behavior = {
          id: behavior.id,
          name: behavior.name,
          ...behavior.definition.behavior,
        };
      });
    }
  }

  const metricBehavior = metric.definition.behavior;

  // manage sharing and permissions action
  if (canShare) {
    actions.push({
      name: ActionType.ShareMetric,
      label: ({t}) => t(`query-builder.context-menu.share`),
      icon: `invite`,
      // Only superadmin or editor can share
      disabled: !getShareCapability(metric, EntityType.Metric).enableShare,
      disabledTooltipText: ({t}) => t(`query-builder.context-menu.share-no-access`),
      item: {
        id: metric.id,
        superadminOverrideShare: getShareCapability(metric).requireSuperadminOverride,
      },
    });
  }

  // view entity in lexicon link
  links.push({
    icon: `share`,
    href:
      getReportUrl(`lexicon`, {projectId: getProjectId(), workspaceId: getWorkspaceId()}) +
      `/transformations/metrics/${metric.id}`,
    tooltipText: ({t}) => t(`query-builder.context-menu.view-in-lexicon`),
  });

  actions.push({
    name: ActionType.ViewEntity,
    label: ({t}) => t(`query-builder.context-menu.view-in-lexicon`),
    icon: `share`,
    item: {
      id: metric.id,
      name: metric.name,
      url:
        getReportUrl(`lexicon`, {projectId: getProjectId(), workspaceId: getWorkspaceId()}) +
        `/transformations/metrics/${metric.id}`,
    },
  });

  if (metric.created_by) {
    details.push({
      name: ({t}) => t(`query-builder.context-menu.creator`),
      type: DetailType.User,
      value: {...metric.created_by},
    });
  }

  if (
    canvasEnabled &&
    metric.owned_by &&
    projectUsers.getAllValues().find((member) => member.email === metric.owned_by.email)
  ) {
    details.push({
      name: ({t}) => t(`query-builder.context-menu.owner`),
      type: DetailType.User,
      value: {...metric.owned_by},
    });
  }

  const definitionDetails: ContextPanelData[`details`] = [];

  definitionDetails.push({
    name: ({t}) => t(`query-builder.context-menu.data-group`),
    value: i18n.print(
      getSelectedGroupLabel({
        dataGroupId: metric.definition.measurement.dataGroupId,
        dataGroups,
      }),
    ),
  });

  definitionDetails.push({
    name: ({t}) => t(`query-builder.context-menu.measurement`),
    value: i18n.print(
      getSelectedCountByLabel({
        selected: getCountByForMetric(metric),
        metricType: metricBehavior.type,
        dataGroupId: metric.definition.measurement.dataGroupId,
        dataGroups,
        withDataGroupSelect: true,
      }),
    ),
  });

  let definitionRenderer: ContextPanelData[`definitionRenderer`];
  // exclude things like user profiles and cohorts
  if (isSaveableBehavior(metricBehavior)) {
    definitionRenderer = () => {
      return jsx(BehaviorDefinitionPanelEl[`panelTagName`] as string, {
        params: {
          props: {
            behavior: metricBehavior,
            excludeBehaviorName: false,
          } satisfies BehaviorDefinitionPanelProps,
        },
      });
    };
  }

  return {
    actions,
    links,
    header: metric.name,
    subHeader: ({t}) => t(`query-builder.metrics.saved-metric`),
    entityIcon: getIconForSavedMetric(metric),
    backgroundColor: getBackgroundColorForMetric(getMetricBehaviorType(metric)),
    description: metric.description,
    entityType: EntityType.Metric,
    excludedMetricIcons: [`pie`, `pulse`],
    definitionDetails,
    definitionRenderer,
    details,
    // allows for editing the description of the metric
    editItem:
      canWriteMetrics && permissions.enableEdit
        ? {
            id: metric.id,
            type: EntityType.Metric,
            name: metric.name,
          }
        : null,
    verified: metric.verified,
    verifiedAt: metric.last_verified,
    verifiedBy: metric.last_verified_by?.name || metric.last_verified_by?.email,
  };
}
